import React, { useState } from 'react';
import {
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { AUDIT_LOG_DESCRIPTIONS, TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';

const AuditLogsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    sort,
    sortDir,
    setSorting,
}) => {
    const { t } = useTranslation();
    const [expandedRows, setExpandedRows] = useState([]);

    const handleExpandClick = (rowIndex) => {
        if (expandedRows.includes(rowIndex)) {
            setExpandedRows(expandedRows.filter((row) => row !== rowIndex));
        } else {
            setExpandedRows([...expandedRows, rowIndex]);
        }
    };

    const getLogDescription = (logType, logData) => {
        // Bulk affiliate migrations share their log_type with single-transaction
        // changes; the log_data.bulk flag is the only thing distinguishing them
        // in storage, so the description needs to read from the row payload.
        if (logType === 'change_transaction_payment_type' && logData?.bulk) {
            return 'A transaction payment type has been changed (bulk migration).';
        }
        const logDescription = AUDIT_LOG_DESCRIPTIONS[logType] || t('auditlogs.unknownLogType');
        return logDescription;
    };

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer
                sx={{
                    // Cap the height so the table scrolls inside its own region and the
                    // sticky header pins to the top. Desktop offset (256px = app bar +
                    // title + filter row + pagination) keeps it single-scroll; mobile falls
                    // back to a viewport fraction since the filter wraps and grows.
                    maxHeight: { xs: '60dvh', md: 'calc(100dvh - 256px)' },
                }}
            >
                <Table stickyHeader sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('auditlogs.columns.venueName')}</TableCell>
                            <TableCell>{t('auditlogs.columns.userName')}</TableCell>
                            <SortableTableCell
                                arrow={sort === 'log_type' ? sortDir : ''}
                                onClick={() => setSorting('log_type')}
                            >
                                {t('auditlogs.columns.logType')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'created_at' ? sortDir : ''}
                                onClick={() => setSorting('created_at')}
                            >
                                {t('auditlogs.columns.created')}
                            </SortableTableCell>
                            <TableCell>{t('auditlogs.columns.logData')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={6} />
                        ) : (
                            rows?.map((row, index) => (
                                <React.Fragment key={row.id}>
                                    <TableRow>
                                        <TableCell>{row.venue_name || '-'}</TableCell>
                                        <TableCell>{row.user_name || '-'}</TableCell>
                                        <TableCell>{row.log_type}</TableCell>
                                        <TableCell>
                                            {formatDateTimeInVenueTz(row.created_at)}
                                        </TableCell>
                                        <TableCell>
                                            {expandedRows.includes(index) ? (
                                                <pre
                                                    style={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordWrap: 'break-word',
                                                    }}
                                                >
                                                    {JSON.stringify(row.log_data, null, 2)}
                                                </pre>
                                            ) : (
                                                getLogDescription(row.log_type, row.log_data)
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip
                                                title={
                                                    !expandedRows.includes(index)
                                                        ? t('auditlogs.expandForDetailed')
                                                        : t('auditlogs.closeForBriefVersion')
                                                }
                                                placement="top"
                                            >
                                                <IconButton
                                                    onClick={() => handleExpandClick(index)}
                                                >
                                                    {!expandedRows.includes(index) ? (
                                                        <ExpandMore />
                                                    ) : (
                                                        <ExpandLess />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default AuditLogsTable;
